import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, BookOpen, AlertTriangle, Download, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import config from '../config/config';

const JournalEntries = () => {
  const [entries, setEntries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [filter, setFilter] = useState({ start: '', end: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      setError('No authentication token found.');
      setLoading(false);
      return;
    }

    axios.get(config.API_URL+'/api/pullJournalEntries', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(response => {
      const data = response.data.data;
      setEntries(data);
      setFiltered(data);
      calculateTotals(data);
      setLoading(false);
    })
    .catch(() => {
      setError('Failed to load journal entries. Check your token or API.');
      setLoading(false);
    });
  }, []);

  const calculateTotals = (data) => {
    const debitTotal = data.reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0);
    const creditTotal = data.reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0);
    setTotals({ debit: debitTotal, credit: creditTotal });
  };

  const applyFilter = () => {
    if (!filter.start || !filter.end) return;

    const from = new Date(filter.start);
    const to = new Date(filter.end);

    const result = entries.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate >= from && entryDate <= to;
    });

    setFiltered(result);
    calculateTotals(result);
  };

  const exportToExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(filtered.map(e => ({
      Date: new Date(e.date).toLocaleDateString(),
      Account: e.account?.name || `Account #${e.account_id}`,
      Debit: e.debit || 0,
      Credit: e.credit || 0,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Journal');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Journal_Entries.xlsx');
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const logo = new Image();
    logo.src = '/logo.png'; // place your logo in public folder

    logo.onload = () => {
      doc.addImage(logo, 'PNG', 14, 10, 30, 15); // adjust logo size/position
      doc.text('Journal Entries Report', 105, 20, null, null, 'center');

      autoTable(doc, {
        startY: 30,
        head: [['Date', 'Account', 'Debit (KES)', 'Credit (KES)']],
        body: filtered.map(entry => [
          new Date(entry.date).toLocaleDateString(),
          entry.account?.name || `Account #${entry.account_id}`,
          entry.debit || '0',
          entry.credit || '0'
        ]),
        foot: [[
          'Total',
          '',
          totals.debit.toLocaleString(),
          totals.credit.toLocaleString()
        ]]
      });

      doc.save('Journal_Entries.pdf');
    };
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin w-6 h-6 text-blue-600" /></div>;
  }

  if (error) {
    return <div className="text-center text-red-600 flex justify-center items-center gap-2 mt-10"><AlertTriangle className="w-5 h-5" /><span>{error}</span></div>;
  }

  return (
    <div className="max-w-10xl mx-auto p-6 bg-white rounded-2xl shadow-lg mt-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-500" />
            Journal Entries
          </h2>
          <span className="text-sm text-gray-500">({filtered.length} shown)</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={filter.start} onChange={e => setFilter({ ...filter, start: e.target.value })} className="border px-2 py-1 rounded text-sm" />
          <input type="date" value={filter.end} onChange={e => setFilter({ ...filter, end: e.target.value })} className="border px-2 py-1 rounded text-sm" />
          <button onClick={applyFilter} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">Apply Filter</button>
          <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100 text-left text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Debit (KES)</th>
              <th className="px-4 py-3 text-right">Credit (KES)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50 transition">
                <td className="px-4 py-2 text-gray-700">{new Date(entry.date).toLocaleDateString()}</td>
                <td className="px-4 py-2 font-medium text-gray-800">{entry.name || `Account #${entry.account_id}`}</td>
                <td className="px-4 py-2 text-right text-green-600 font-semibold">{entry.description || '-'}</td>
                  <td className="px-4 py-2 text-right text-green-600 font-semibold">{entry.debit || '-'}</td>
                <td className="px-4 py-2 text-right text-red-600 font-semibold">{entry.credit || '-'}</td>
              </tr>
            ))}
            <tr className="bg-gray-100 font-semibold">
              <td colSpan="3" className="px-4 py-2 text-right">Total</td>
              <td className="px-4 py-2 text-right text-green-700">{totals.debit.toLocaleString()}</td>
              <td className="px-4 py-2 text-right text-red-700">{totals.credit.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JournalEntries;
